import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent,
  ListItemIcon,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import TranslateIcon from '@mui/icons-material/Translate';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import GroupsIcon from '@mui/icons-material/Groups';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import ChangelogDialog from './ChangelogDialog';
import { useLanguage } from '../context/LanguageContext';
import AdminPanel from './AdminPanel';
import OutreachStatistics from './OutreachStatistics';
import UserSettingsDialog from './UserSettingsDialog';
import SalesforceIntegration from './SalesforceIntegration';
import HubSpotIntegration from './HubSpotIntegration';
import api from '../api';
import logger from './../utils/logger.js';
import { HEADER_OFFSET } from '../constants/layout';

// Check if we're in development environment
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';

const Header = ({ user: userProp, isAdmin: isAdminProp, onNavigateHome }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout, user: authUser, isAdmin: authIsAdmin, isSupervisor, team, subscriptionStatus, triggerSubscriptionModal, isSubscriptionModalActive } = useAuth();
  const { getSubscription, needsSubscription, isTrialExpired, getDaysRemaining } = useSubscription();
  const { t, language, isNL } = useLanguage();
  
  // Debug the translation issue
  logger.log("Current language:", language);
  logger.log("appTitle translation:", t('appTitle'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [teamPerformanceOpen, setTeamPerformanceOpen] = useState(false);
  const [salesforceOpen, setSalesforceOpen] = useState(false);
  const [salesforceTab, setSalesforceTab] = useState(0);
  const [salesforceConnected, setSalesforceConnected] = useState(false);
  const [checkingSalesforce, setCheckingSalesforce] = useState(false);
  const [hubspotOpen, setHubspotOpen] = useState(false);
  const [hubspotTab, setHubspotTab] = useState(0);
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [checkingHubspot, setCheckingHubspot] = useState(false);
  
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);
  const [userSettingsTab, setUserSettingsTab] = useState(0);
  const [scrollToAddOns, setScrollToAddOns] = useState(false);
  
  const user = userProp || authUser;
  const isAdmin = isAdminProp !== undefined ? isAdminProp : authIsAdmin;
  
  // Listen for custom event to open user settings from IntegrationBanner
  useEffect(() => {
    const handleOpenUserSettingsEvent = (event) => {
      const tab = event.detail?.tab || 0;
      const shouldScroll = event.detail?.scrollToAddOns || false;
      setUserSettingsTab(tab);
      setScrollToAddOns(shouldScroll);
      setUserSettingsOpen(true);
    };
    
    window.addEventListener('openUserSettings', handleOpenUserSettingsEvent);
    
    return () => {
      window.removeEventListener('openUserSettings', handleOpenUserSettingsEvent);
    };
  }, []);
  
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    await logout();
    handleCloseMenu();
  };
  
  const handleSwitchAccount = async () => {
    // Logout with forceNewLogin=true to clear session and force new login selection
    await logout(true);
    handleCloseMenu();
  };
  
  const handleOpenChangelog = () => {
    setChangelogOpen(true);
  };
  
  const handleCloseChangelog = () => {
    setChangelogOpen(false);
  };


  const handleOpenAdminPanel = () => {
    setAdminPanelOpen(true);
    handleCloseMenu();
  };

  const handleCloseAdminPanel = () => {
    setAdminPanelOpen(false);
  };

  const handleOpenUserSettings = () => {
    setUserSettingsTab(0);
    setScrollToAddOns(false);
    setUserSettingsOpen(true);
    handleCloseMenu();
  };

  const handleCloseUserSettings = () => {
    setUserSettingsOpen(false);
    setUserSettingsTab(0);
    setScrollToAddOns(false);
  };

  const handleOpenSalesforce = (tab = 0) => {
    // Ensure tab is always a number (0 or 1)
    const normalizedTab = typeof tab === 'number' ? tab : (typeof tab === 'string' ? parseInt(tab, 10) : 0);
    const safeTab = (normalizedTab === 0 || normalizedTab === 1) ? normalizedTab : 0;
    setSalesforceTab(safeTab);
    setSalesforceOpen(true);
    handleCloseMenu();
  };
  
  const handleCloseSalesforce = () => {
    setSalesforceOpen(false);
    setSalesforceTab(0); // Reset to connection tab when closing
  };
  
  const handleOpenHubspot = (tab = 0) => {
    // Ensure tab is always a number (0 or 1)
    const normalizedTab = typeof tab === 'number' ? tab : (typeof tab === 'string' ? parseInt(tab, 10) : 0);
    const safeTab = (normalizedTab === 0 || normalizedTab === 1) ? normalizedTab : 0;
    setHubspotTab(safeTab);
    setHubspotOpen(true);
    handleCloseMenu();
  };
  
  const handleCloseHubspot = () => {
    setHubspotOpen(false);
    setHubspotTab(0); // Reset to connection tab when closing
  };
  
  // Check Salesforce connection status
  useEffect(() => {
    const checkSalesforceStatus = async () => {
      if (!user) {
        setSalesforceConnected(false);
        return;
      }
      
      try {
        setCheckingSalesforce(true);
        const result = await api.salesforceStatus();
        setSalesforceConnected(result.success && result.connected === true);
      } catch (err) {
        logger.error('Error checking Salesforce status in Header:', err);
        setSalesforceConnected(false);
      } finally {
        setCheckingSalesforce(false);
      }
    };
    
    checkSalesforceStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkSalesforceStatus, 30000);
    
    return () => clearInterval(interval);
  }, [user]);
  
  // Check HubSpot connection status
  useEffect(() => {
    const checkHubspotStatus = async () => {
      if (!user) {
        setHubspotConnected(false);
        return;
      }
      
      try {
        setCheckingHubspot(true);
        const result = await api.hubspotStatus();
        setHubspotConnected(result.success && result.connected === true);
      } catch (err) {
        logger.error('Error checking HubSpot status in Header:', err);
        setHubspotConnected(false);
      } finally {
        setCheckingHubspot(false);
      }
    };
    
    checkHubspotStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkHubspotStatus, 30000);
    
    return () => clearInterval(interval);
  }, [user]);


  const handleUpgradeClick = () => {
    if (!isSubscriptionModalActive) {
      triggerSubscriptionModal(subscriptionStatus, 'Header upgrade button');
    }
  };

  // Check if user needs subscription
  const shouldShowUpgradeButton = subscriptionStatus && (
    subscriptionStatus.status === 'trial' ||
    subscriptionStatus.status === 'expired' ||
    subscriptionStatus.status === 'cancelled' ||
    !subscriptionStatus.status ||
    (subscriptionStatus.status === 'trial' && subscriptionStatus.days_remaining <= 7)
  );


  // Show notification to user
  const showNotification = (message, severity = 'error') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Handle closing notification
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  const handleHomeClick = () => {
    if (onNavigateHome) {
      onNavigateHome();
    }
  };


  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: isDevelopment ? '#ff9800' : '#1976d2',
          color: isDevelopment ? '#000000' : '#ffffff',
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: 48, sm: 64 },
          px: { xs: 1, sm: 2 },
          overflow: 'hidden',
          flexWrap: 'nowrap'
        }}>
          <Box
            component="button"
            type="button"
            onClick={handleHomeClick}
            aria-label="Go to home"
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'inherit',
              p: 0,
              mr: { xs: 0.5, sm: 1 },
              flexShrink: 0,
              minWidth: 0,
              '&:hover': { opacity: 0.85 },
            }}
          >
            <Box
              component="img"
              src="/salinator-logo.png"
              alt=""
              aria-hidden
              sx={{
                display: { xs: 'flex', md: 'flex' },
                mr: { xs: 0.5, sm: 1 },
                flexShrink: 0,
                height: { xs: 24, sm: 28, md: 32 },
                width: 'auto',
                filter: isDevelopment ? 'brightness(0)' : 'brightness(0) invert(1)',
              }}
            />
            <Typography
              variant="h5"
              noWrap
              component="span"
              sx={{
                display: 'flex',
                flexShrink: 0,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: { xs: 600, md: 500 },
                alignItems: 'center',
              }}
            >
              {t('appTitle')}
            </Typography>
          </Box>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{
              ml: { xs: 0.5, sm: 1 },
              display: { xs: 'none', sm: 'block' },
              backgroundColor: isDevelopment ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)',
              px: { xs: 0.5, sm: 1 },
              py: 0.5,
              borderRadius: 1,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }}
          >
            {t('appSubtitle')}
          </Typography>
          
          {/* Development Environment Indicator */}
          {isDevelopment && (
            <Chip
              icon={<CodeIcon sx={{ color: 'inherit !important' }} />}
              label="DEVELOPMENT"
              color="warning"
              variant="filled"
              size="small"
              sx={{ 
                ml: 1, 
                fontWeight: 'bold',
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                color: 'inherit',
                display: { xs: 'none', sm: 'flex' },
                flexShrink: 0,
              }}
            />
          )}

          {/* Spacer for centering the user actions */}
          <Box sx={{ flexGrow: 1, minWidth: { xs: 8, sm: 16 } }} />
          
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.5, sm: 1 }, 
            alignItems: 'center',
            flexShrink: 0,
            minWidth: 0
          }}>
            {user ? (
              <>
                <Tooltip title={t('userMenu')}>
                  <IconButton
                    onClick={handleOpenMenu}
                    size={isMobile ? 'small' : 'medium'}
                    color="inherit"
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    aria-label={t('userMenu')}
                    sx={{
                      padding: { xs: '4px', sm: '8px' },
                    }}
                  >
                    <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={open}
                  onClose={handleCloseMenu}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: { minWidth: 220 },
                  }}
                >
                  <MenuItem disabled>
                    <Typography variant="body2" component="div">
                      {t('loggedInAs')} <strong>{user.display_name || user.username}</strong>
                    </Typography>
                  </MenuItem>
                  
                  {/* Subscription Status */}
                  <MenuItem disabled>
                    <Box sx={{ width: '100%' }}>
                      {getSubscription() && getSubscription().status === 'trial' && !isTrialExpired() && (
                        <Chip 
                          label={`Trial: ${getDaysRemaining()} days left`}
                          color="info"
                          size="small"
                          sx={{ width: '100%' }}
                        />
                      )}
                      {getSubscription() && getSubscription().status === 'active' && (
                        <Chip 
                          label="Salinator Pro"
                          color="success"
                          size="small"
                          sx={{ width: '100%' }}
                        />
                      )}
                      {isTrialExpired() && (
                        <Chip 
                          label="Trial Expired"
                          color="error"
                          size="small"
                          sx={{ width: '100%' }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  
                  <Divider />

                  {shouldShowUpgradeButton && (
                    <MenuItem
                      onClick={() => {
                        handleUpgradeClick();
                        handleCloseMenu();
                      }}
                    >
                      <ListItemIcon>
                        <InfoIcon fontSize="small" color="warning" />
                      </ListItemIcon>
                      {subscriptionStatus?.status === 'expired' || subscriptionStatus?.status === 'cancelled'
                        ? 'Subscribe'
                        : subscriptionStatus?.days_remaining <= 3
                          ? 'Upgrade Now!'
                          : 'Upgrade'}
                    </MenuItem>
                  )}
                  
                  {/* Show Admin Panel option for admins and supervisors */}
                  {(isAdmin || isSupervisor) && (
                    <MenuItem onClick={handleOpenAdminPanel}>
                      <ListItemIcon>
                        <SupervisorAccountIcon fontSize="small" />
                      </ListItemIcon>
                      {t('admin')}
                    </MenuItem>
                  )}
                  {team && !isAdmin && !isSupervisor && (
                    <MenuItem onClick={() => {
                      handleCloseMenu();
                      setTeamPerformanceOpen(true);
                    }}>
                      <ListItemIcon>
                        <GroupsIcon fontSize="small" />
                      </ListItemIcon>
                      Team performance
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => handleOpenSalesforce(salesforceConnected ? 1 : 0)}>
                    <ListItemIcon>
                      <CloudDoneIcon fontSize="small" sx={{ color: '#00A1E0' }} />
                    </ListItemIcon>
                    Salesforce{salesforceConnected ? ' (Connected)' : ''}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenHubspot(hubspotConnected ? 1 : 0)}>
                    <ListItemIcon>
                      <CloudDoneIcon fontSize="small" sx={{ color: '#FF7A59' }} />
                    </ListItemIcon>
                    HubSpot{hubspotConnected ? ' (Connected)' : ''}
                  </MenuItem>
                  <MenuItem onClick={handleSwitchAccount}>
                    <ListItemIcon>
                      <SwitchAccountIcon fontSize="small" />
                    </ListItemIcon>
                    {t('switchAccount')}
                  </MenuItem>
                  <MenuItem onClick={handleOpenUserSettings}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    {t('settings')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    {t('logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button 
                color="inherit"
                startIcon={<LoginIcon />}
                sx={{ 
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                {t('login')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ height: HEADER_OFFSET, flexShrink: 0 }} />
      
      <ChangelogDialog 
        open={changelogOpen} 
        onClose={handleCloseChangelog} 
      />


      {/* Admin Panel Dialog */}
      <Dialog
        open={adminPanelOpen}
        onClose={handleCloseAdminPanel}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            height: "90vh",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}>
          <Box display="flex" alignItems="center">
            <SupervisorAccountIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('admin')}</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseAdminPanel}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: "flex", 
          flexDirection: "column", 
          flexGrow: 1,
          overflow: "hidden" 
        }}>
          <AdminPanel />
        </DialogContent>
      </Dialog>

      <Dialog
        open={teamPerformanceOpen}
        onClose={() => setTeamPerformanceOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}>
          <Box display="flex" alignItems="center">
            <GroupsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{team?.name || 'Team performance'}</Typography>
          </Box>
          <IconButton aria-label="close" onClick={() => setTeamPerformanceOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, overflow: 'auto' }}>
          {teamPerformanceOpen && <OutreachStatistics />}
        </DialogContent>
      </Dialog>

      {/* User Settings Dialog */}
      <UserSettingsDialog
        open={userSettingsOpen}
        onClose={handleCloseUserSettings}
        initialTab={userSettingsTab}
        scrollToAddOns={scrollToAddOns}
      />

      {/* Salesforce Integration Dialog */}
      <Dialog
        open={salesforceOpen}
        onClose={handleCloseSalesforce}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            height: "90vh",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}>
          <Box display="flex" alignItems="center">
            <CloudDoneIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Salesforce Integration</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseSalesforce}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: "flex", 
          flexDirection: "column", 
          flexGrow: 1,
          overflow: "auto" 
        }}>
          <SalesforceIntegration initialTab={salesforceTab} />
        </DialogContent>
      </Dialog>

      {/* HubSpot Integration Dialog */}
      <Dialog
        open={hubspotOpen}
        onClose={handleCloseHubspot}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? "100%" : "auto",
            maxHeight: isMobile ? "100%" : "90vh",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}>
          <Box display="flex" alignItems="center">
            <CloudDoneIcon sx={{ mr: 1, color: '#FF7A59' }} />
            <Typography variant="h6">HubSpot Integration</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseHubspot}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: "flex", 
          flexDirection: "column", 
          flexGrow: 1,
          overflow: "auto" 
        }}>
          <HubSpotIntegration initialTab={hubspotTab} />
        </DialogContent>
      </Dialog>

      {/* Add notification for errors */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;